/*  */// web-staking-estimator-frontend/src/hooks/map/useNearestFeature.js
import { useRef, useEffect, useCallback, useState } from "react";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { toGeometry, clampScale } from "../../services/map/geometryService";
import {
  hideOverlays,
  basemapSnapshot,
  waitBasemapLayerViewsReady,
  waitOverlayReady,
  pngDataUrlToJpeg,
  sleep,
} from "../../services/map/screenshotService";
import { loadUiCfg, connectorSymbolFromCfg } from "../../services/map/configService";
import { pruneUserPoints } from "../../utils/map/graphicsHelpers";

const FEET_PER_METER = 3.280839895;

/**
 * Hook to manage nearest infrastructure feature detection
 * Handles finding closest features, drawing connectors, and capturing screenshots
 */
export function useNearestFeature({
  viewRef,
  clickLayerRef,
  resultsLayerRef,
  requiredPhase,
  maxRadiusMeters,
  setEstimate,
  setSelectedPoint,
  onRadiusUpdate,
  onToast,
  onNearestSummary,
  onOpIdChange,
  onSearchingChange,
}) {
  const [isSearching, setIsSearching] = useState(false);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const pendingArgsRef = useRef(null);
  const opIdRef = useRef(0);
  const pendingHintRef = useRef(null);
  const lastPointRef = useRef(null);
  const phaseRef = useRef(null);
  const radiusCbRef = useRef(onRadiusUpdate);
  const toastCbRef = useRef(onToast);
  const savedZoomRef = useRef(null);

  // Track latest requiredPhase
  useEffect(() => {
    const n = Number(requiredPhase);
    phaseRef.current = Number.isFinite(n) ? n : null;
  }, [requiredPhase]);

  useEffect(() => {
    radiusCbRef.current = onRadiusUpdate;
  }, [onRadiusUpdate]);
  
  useEffect(() => {
    toastCbRef.current = onToast;
  }, [onToast]);

  /**
   * Capture map screenshot on-demand (for PDF generation)
   * This is called ONLY when user clicks "Generate My Estimate"
   */
  const captureMapSnapshot = useCallback(async () => {
    const view = viewRef.current;
    const resultsLayer = resultsLayerRef.current;
    
    if (!view) {
      console.warn("[MC:screenshot] No view available");
      return null;
    }

    try {
      // Restore saved zoom state (from pin drop) before screenshot
      if (savedZoomRef.current) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log("[MC:screenshot] Restoring saved zoom state:", { 
              scale: savedZoomRef.current.scale, 
              zoom: savedZoomRef.current.zoom 
            });
          }
          
          await view.goTo(
            { 
              target: savedZoomRef.current.center,
              scale: savedZoomRef.current.scale
            },
            { duration: 500, easing: "ease" }
          );
          
          // Wait for animation to complete
          await sleep(200);
          if (process.env.NODE_ENV === 'development') {
            console.log("[MC:screenshot] Zoom restored successfully");
          }
        } catch (e) {
          console.warn("[MC:screenshot] Zoom restore failed:", e);
          // Continue with screenshot anyway
        }
      } else {
        console.warn("[MC:screenshot] No saved zoom state available, using current view");
      }
      
      hideOverlays(true);
      await sleep(50);

      /*
       * Had bug where user sees a flicker when PDF was generated
       * ArcGIS JS API - on basemap changes - must wait for layers to load
       * Temporarily switch basemap for PDF screenshot
       * We save the original basemap and restore it after screenshot
       */
      let prevBasemap = null;
      try {
        const uiCfg = await loadUiCfg();
        const debug = !!uiCfg?.pdf?.debug;
        const fb = uiCfg?.pdf?.forceBasemap || "hybrid";
        if (debug) console.log("[pdf] before switch", { fb }, basemapSnapshot(view));
        
        if (view?.map && fb) {
          prevBasemap = view.map.basemap;
          view.map.basemap = fb;
          
          if (debug) console.log("[pdf] after set", basemapSnapshot(view));
          await waitBasemapLayerViewsReady(view, 12000, debug);
          
          if (debug) console.log("[pdf] after wait", basemapSnapshot(view));
        }

        await waitOverlayReady(view, 10000, 1, false);
      } catch (e) {
        console.warn("[pdf] readiness wait error:", e);
      }

      const shot = await view.takeScreenshot({ width: 640, format: "png" });

      try {
        if (prevBasemap && view?.map) {
          view.map.basemap = prevBasemap;
          const uiCfg = await loadUiCfg();
          if (uiCfg?.pdf?.debug)
            console.log("[pdf] restored previous basemap", basemapSnapshot(view));
        }
      } catch {}

      hideOverlays(false);

      if (shot && shot.dataUrl) {
        const jpegDataUrl = await pngDataUrlToJpeg(shot.dataUrl, 640, 0.7);
        
        if (process.env.NODE_ENV === 'development') {
          console.log("[MC:screenshot] SUCCESS - Captured for PDF");
        }
        return jpegDataUrl;
      }
      
      console.warn("[MC:screenshot] No screenshot data returned");
      return null;
    } catch (e) {
      hideOverlays(false);
      console.error("[MC:screenshot] FAILED:", e);
      throw e;
    }
  }, [viewRef]);

  const runNearestForPoint = useCallback(
    async (clickPoint, { emitSelectedPoint = false } = {}) => {
      if (!clickPoint) return;

      const view = viewRef.current;
      const clickLayer = clickLayerRef.current;
      const resultsLayer = resultsLayerRef.current;
      
      if (!view || !clickLayer || !resultsLayer) return;

      // If busy, queue newest request and abort current
    /*
     * Handle rapid-fire requests (user clicking around map quickly)
     * If request is in-flight and new request comes in:
     *   1. Save new request to pendingArgs
     *   2. Abort current request
     *   3. When abort finishes, start pending request
     * This prevents race conditions where slow request overwrites fast request
     */
    if (inFlightRef.current) {
      pendingArgsRef.current = { clickPoint, emitSelectedPoint };
      try {
        abortRef.current?.abort();
      } catch {}
      return;
    }

      inFlightRef.current = true;
      setIsSearching(true);
      try {
        onSearchingChange?.(true);
      } catch {}

      const myOpId = ++opIdRef.current;
      pendingHintRef.current = null;
      try {
        onOpIdChange?.(myOpId);
      } catch {}

      try {
        radiusCbRef.current?.(maxRadiusMeters);
      } catch {}

      const viewSR = view.spatialReference;

      // Clear result graphics
      resultsLayer.removeAll();

      // On phase-change requery, prune old connectors but keep user points
      if (!emitSelectedPoint && clickLayer) {
        try {
          const keep = [];
          clickLayer.graphics.forEach((g) => {
            if (g?.geometry?.type === "point") keep.push(g);
          });
          if (keep.length !== clickLayer.graphics.length) {
            if (process.env.NODE_ENV === 'development') {
              console.log(
                "[MC:cleanup] pruning",
                clickLayer.graphics.length - keep.length,
                "old connector(s)"
              );
            }
          }
          clickLayer.removeAll();
          keep.forEach((g) => clickLayer.add(g));
        } catch {}
      }

      const rp = phaseRef.current;

      if (process.env.NODE_ENV === 'development') {
        console.log("[MC:run]", {
          x: clickPoint?.x,
          y: clickPoint?.y,
          wkid: viewSR?.wkid,
          requiredPhase: rp,
        });
      }

      // Orange user point
      if (emitSelectedPoint && myOpId === opIdRef.current) {
        pruneUserPoints(clickLayer);
        pruneUserPoints(resultsLayer);

        const clickGraphic = new Graphic({
          geometry: clickPoint,
          attributes: { __userPoint: true },
          symbol: new SimpleMarkerSymbol({
            color: [226, 119, 40],
            size: 10,
            outline: { color: [255, 255, 255], width: 1 },
          }),
        });
        clickLayer.add(clickGraphic);

        setSelectedPoint({
          latitude: clickPoint.latitude,
          longitude: clickPoint.longitude,
          x: clickPoint.x,
          y: clickPoint.y,
          wkid: viewSR?.wkid ?? 3857,
        });
      }

      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
      }
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const url = new URL("/api/structures/nearest", window.location.origin);
        url.searchParams.set("x", clickPoint.x);
        url.searchParams.set("y", clickPoint.y);
        url.searchParams.set("wkid", viewSR?.wkid ?? 3857);
        url.searchParams.set("returnAll", "true");
        url.searchParams.set("pageSize", "1000");
        url.searchParams.set("maxResults", "25");
        if (rp != null) {
          url.searchParams.set("requiredPhase", String(rp));
        }

        if (process.env.NODE_ENV === 'development') {
          console.log("[MC:url]", url.toString());
        }

        const resp = await fetch(url.toString(), { signal: controller.signal });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const nearest = await resp.json();

        if (myOpId !== opIdRef.current) return;

        if (process.env.NODE_ENV === 'development') {
          console.log("[MC:nearest]", {
            ok: nearest?.ok,
            strategy: nearest?.strategy,
            features: Array.isArray(nearest?.features) ? nearest.features.length : 0,
            all: Array.isArray(nearest?.featuresAll) ? nearest.featuresAll.length : 0,
            requiredPhase: rp,
          });
        }

        const featuresNearest = Array.isArray(nearest?.features) ? nearest.features : [];
        const useFiltered = rp != null;
        const featuresForEval = useFiltered
          ? featuresNearest
          : Array.isArray(nearest?.featuresAll) && nearest.featuresAll.length
          ? nearest.featuresAll
          : featuresNearest;

        if (!nearest?.ok || featuresForEval.length === 0) {
          pendingHintRef.current = { xfmrOHUG: null, infraPhase: null, noneFound: true };
          if (process.env.NODE_ENV === 'development') {
            console.log("[MC:bufferHint] noneFound");
          }
          return;
        }

        const infraGeoms = featuresForEval
          .map((f) => toGeometry(f.geometry, viewSR))
          .filter(Boolean);

        let closestGeom = null;
        let shortestMeters = Infinity;
        let closestIdx = -1;
        for (let i = 0; i < infraGeoms.length; i++) {
          const g = infraGeoms[i];
          const d = geometryEngine.distance(clickPoint, g, "meters");
          if (typeof d === "number" && isFinite(d) && d < shortestMeters) {
            shortestMeters = d;
            closestGeom = g;
            closestIdx = i;
          }
        }

        // Buffer nearest summary
        try {
          const primary = closestIdx >= 0 ? featuresForEval[closestIdx] : null;
          const attrs = primary?.attributes || {};
          const xfmrOHUG =
            attrs?.line_orientation ?? attrs?.Placement ?? attrs?.placement ?? null;
          const phRaw = attrs?.phase_num ?? attrs?.Phase ?? attrs?.phase ?? null;
          const infraPhase = Number.isFinite(Number(phRaw)) ? Number(phRaw) : null;

          const noneFound =
            !Array.isArray(featuresForEval) ||
            featuresForEval.length === 0 ||
            !closestGeom ||
            !isFinite(shortestMeters);

          const hint = {
            xfmrOHUG: xfmrOHUG ? String(xfmrOHUG).trim().toUpperCase() : null,
            infraPhase,
            noneFound,
          };
          pendingHintRef.current = hint;

          const feet = isFinite(shortestMeters) ? Math.round(shortestMeters * FEET_PER_METER) : null;
          if (process.env.NODE_ENV === 'development') {
            console.log("[MC:bufferHint]", { ...hint, feet, idx: closestIdx });
          }
        } catch (e) {
          console.warn("[MC:bufferHint error]", e?.message || e);
        }

        // Draw blue dot + connector
        let connector = null;
        if (closestGeom && isFinite(shortestMeters) && myOpId === opIdRef.current) {
          const near = geometryEngine.nearestCoordinate(closestGeom, clickPoint);

          const nearestDot = new Graphic({
            geometry: near.coordinate,
            symbol: {
              type: "simple-marker",
              size: 9,
              color: [0, 120, 255, 0.95],
              outline: { color: [255, 255, 255], width: 1 },
            },
          });
          resultsLayer.add(nearestDot);

          // Debug label
          try {
            if (typeof window !== "undefined" && window.DEBUG_STAKING) {
              const label = new Graphic({
                geometry: near.coordinate,
                symbol: new TextSymbol({
                  text: `${
                    featuresForEval[closestIdx]?.attributes?.phase_num ?? "?"
                  }φ • ${Math.round(shortestMeters * FEET_PER_METER).toLocaleString()} ft`,
                  haloColor: [255, 255, 255, 1],
                  haloSize: 1,
                  font: { size: 10, family: "sans-serif", weight: "bold" },
                }),
              });
              resultsLayer.add(label);
            }
          } catch {}

          // Connector line
          let sym = { type: "simple-line", style: "dash", width: 2, color: [0, 120, 255, 1] };
          try {
            const uiCfg = await loadUiCfg();
            sym = connectorSymbolFromCfg(uiCfg);
          } catch {}

          connector = new Graphic({
            geometry: new Polyline({
              paths: [
                [clickPoint.x, clickPoint.y],
                [near.coordinate.x, near.coordinate.y],
              ],
              spatialReference: clickPoint.spatialReference,
            }),
            symbol: sym,
          });

          resultsLayer.add(connector);
        }

        /*
         * Zoom map based on distance to infrastructure
         * Close distance (800 scale)
         * Medium distance (1500-3000 scale)
         * Far distance (full connector, expand extent 20%)
         * Was sluggish without this, feels better now
         */
        try {
          if (myOpId === opIdRef.current) {
            const connExtent = connector?.geometry?.extent ?? null;
            if (connExtent) {
              const feet = shortestMeters * FEET_PER_METER;
              if (feet >= 5000) {
                await view.goTo(
                  { target: connExtent.expand(1.2) },
                  { duration: 600, easing: "ease" }
                );
              } else {
                let targetScale;
                if (feet < 150) targetScale = 800;
                else if (feet < 500) targetScale = 1500;
                else if (feet < 1500) targetScale = 3000;
                else targetScale = 6000;
                const center = connExtent.center ?? clickPoint;
                await view.goTo(
                  { target: center, scale: clampScale(targetScale) },
                  { duration: 500, easing: "ease" }
                );
              }
              
              // Save zoom state for PDF screenshot
              savedZoomRef.current = {
                center: view.center.clone(),
                scale: view.scale,
                zoom: view.zoom
              };
              if (process.env.NODE_ENV === 'development') {
                console.log("[MC:zoom] Saved zoom state:", { scale: view.scale, zoom: view.zoom });
              }
            }
          }
        } catch {}

        // Screenshot will be captured on-demand when generating PDF
        if (process.env.NODE_ENV === 'development') {
          console.log("[MC:screenshot] Skipped during nearest search - will be captured on-demand");
        }
      } catch (err) {
        // Silently ignore AbortError (it's intentional cancellation, not an error)
        if (err.name === "AbortError") {
          if (process.env.NODE_ENV === 'development') {
            console.log("[MC:nearest] Request aborted (expected behavior)");
          }
        } else {
          console.error("Nearest API error:", err);
          try {
            toastCbRef.current?.("Error calculating estimate.", "err", 5000);
          } catch {}
        }
        pendingHintRef.current = { xfmrOHUG: null, infraPhase: null, noneFound: true };
      } finally {
        setIsSearching(false);
        inFlightRef.current = false;
        try {
          radiusCbRef.current?.(null);
        } catch {}
        try {
          onSearchingChange?.(false);
        } catch {}

        // Emit buffered hint
        if (opIdRef.current === myOpId && pendingHintRef.current) {
          try {
            onNearestSummary?.(pendingHintRef.current);
          } catch {}
        }

        // Process queued request
        const pending = pendingArgsRef.current;
        pendingArgsRef.current = null;
        if (pending) {
          runNearestForPoint(pending.clickPoint, { emitSelectedPoint: false });
        }
      }
    },
    [
      viewRef,
      clickLayerRef,
      resultsLayerRef,
      maxRadiusMeters,
      setEstimate,
      setSelectedPoint,
      onOpIdChange,
      onSearchingChange,
      onNearestSummary,
    ]
  );

  return {
    isSearching,
    runNearestForPoint,
    captureMapSnapshot,
    lastPointRef,
    abortRef,
    pendingArgsRef,
    inFlightRef,
  };
}
